import * as React from "react";
import { cx } from "../internal/cx.js";
import { SystemMessage, type SystemMessageProps } from "../molecules/system-message.js";

/**
 * Chrome treatment for an empty state.
 *
 *   - `dashed` (default) — the house style: a dashed border on a faint
 *     alice-blue ground with the illustration in a tinted circle. Reads as
 *     "nothing here yet, and here's the way in."
 *   - `solid` — the plain filled SystemMessage surface (opt out of the dashed look).
 */
export type EmptyStateEmphasis = "solid" | "dashed";

/**
 * EmptyState — organism-level alias for empty datasets and first-run states.
 *
 * SystemMessage is the molecule that owns the base visual treatment. EmptyState
 * narrows its semantic intent for organism/page composition, gives legacy
 * `*EmptyState`, empty-table, and dashboard-first-run components a stable
 * migration target, and adds the `dashed` house-style emphasis.
 */
export interface EmptyStateProps extends Omit<SystemMessageProps, "tone"> {
  tone?: Extract<SystemMessageProps["tone"], "neutral" | "success">;
  /** Chrome treatment. Defaults to `dashed` (the house style). */
  emphasis?: EmptyStateEmphasis;
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { tone = "neutral", emphasis = "dashed", className, ...props },
  ref,
) {
  return (
    <SystemMessage
      ref={ref}
      tone={tone}
      className={cx(emphasis === "dashed" && "cw-v2-empty-state--dashed", className)}
      {...props}
    />
  );
});
EmptyState.displayName = "EmptyState";

/**
 * ErrorState — paired organism for failed data regions. Use when content
 * could not load; use EmptyState when the load succeeded but returned no data.
 */
export interface ErrorStateProps extends Omit<SystemMessageProps, "tone"> {
  tone?: Extract<SystemMessageProps["tone"], "warning" | "danger">;
}

export const ErrorState = React.forwardRef<HTMLDivElement, ErrorStateProps>(function ErrorState(
  { tone = "danger", ...props },
  ref,
) {
  return <SystemMessage ref={ref} tone={tone} {...props} />;
});
ErrorState.displayName = "ErrorState";
